import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CreateTradeGeneralData,
  Direction,
  FreeTextTradeData,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  OpenConfirmModal,
  Option,
  OrderTypes,
} from "@shared/types";
import {
  GENERAL_OPTION_NAME_TO_CREATE_TRADE_KEY,
  TRADE_TYPE_TO_DISPLAY_NAME,
} from "@shared/features/tradingTerminal/common/constants";
import {
  ExtraInfo,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";
import { ErrorTypes } from "@shared/features/trades/common/types";
import { isEqual } from "lodash";
import {
  findValueByName,
  getOptionsByCreateTradeKey,
} from "@shared/features/tradingTerminal/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

const useSharedGeneralTradeOptions = (openConfirmModal: OpenConfirmModal) => {
  const {
    getCreateGeneralDataQuery,
    generalDataProcessed,
    setGeneralDataProcessed,
    setExtraInfo,
    freeTextTradeData,
    extraInfo,
    useFreeTextData,
    getAvailableQuantityQuery,
    symbols,
    account,
    setShouldUpdatePriceRange,
    setShouldUpdateNumOfOrders,
    prevGeneralOptionsMapping,
    setPrevGeneralOptionsMapping,
    checkOppositePositionError,
    setValuesModified,
    quantity,
    setQuantity,
    setQuantityPercent,
    showedOppositePositionError,
    setShowedOppositePositionError,
    priceTargetsModified,
    generalErrors,
    freeTextProcessed,
    isSignal,
  } = useContext(TradingTerminalContext)!;

  const { currentData: generalData, isFetching: generalDataFetching } =
    getCreateGeneralDataQuery;
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);

  const symbol = symbols?.[0];
  const leverageLimits = generalData?.limits?.leverage;
  const generalOptions = generalData?.data?.general?.options || [];
  const generalInfo = generalData?.data?.general?.info;
  const showGeneralOptions = !!generalOptions.length;
  const availableQuantityLoading = getAvailableQuantityQuery.isFetching;

  const getOptionValueToDisplayMapping = (
    options?: Option[]
  ): Record<number, string> | undefined =>
    options?.reduce((acc, option) => {
      acc[option.value as number] = option.name;
      return acc;
    }, {} as Record<number, string>);

  const getGeneralOptionsMapping = (): CreateTradeGeneralData => {
    const generalOptionsMapping = generalOptions.reduce(
      (acc: any, [name, options, defaultValue]) => {
        let optionValue = defaultValue || options[0].value;
        if (name === "Signal Type") {
          name = "Trade Type";
        }
        const createTradeKey =
          GENERAL_OPTION_NAME_TO_CREATE_TRADE_KEY[name.toLowerCase()];
        if (!createTradeKey) return acc;
        if (createTradeKey === "reverse") {
          optionValue = optionValue === LongShort.SHORT;
          if (checkOppositePositionError(optionValue)) {
            optionValue = !optionValue;
          }
        }
        let freeTextValue =
          useFreeTextData && freeTextProcessed
            ? freeTextTradeData?.[createTradeKey as keyof FreeTextTradeData]
            : undefined;
        let extraInfoValue = extraInfo?.[createTradeKey as keyof ExtraInfo];
        acc[createTradeKey] = freeTextValue ?? extraInfoValue ?? optionValue;
        return acc;
      },
      {}
    );
    generalOptionsMapping["cancel_on_first_target_reached"] = isSignal
      ? null
      : false;
    return generalOptionsMapping;
  };

  const [generalOptionsMapping, setGeneralOptionsMapping] =
    useState<CreateTradeGeneralData>(getGeneralOptionsMapping());

  useEffect(() => {
    if (
      !generalDataFetching &&
      generalOptions.length &&
      !availableQuantityLoading &&
      (!generalDataProcessed || freeTextProcessed)
    ) {
      setGeneralOptionsMapping(getGeneralOptionsMapping());
      setGeneralDataProcessed(true);
    }
  }, [
    generalOptions.length,
    generalDataProcessed,
    generalDataFetching,
    availableQuantityLoading,
    symbol,
    account,
    freeTextProcessed,
  ]);

  const getOptions = useCallback(
    (createTradeKey: string) =>
      getOptionsByCreateTradeKey(
        generalOptions,
        createTradeKey
      ) as Option<number>[],
    [generalOptions]
  );

  const longShortOptions = useMemo(() => getOptions("reverse"), [getOptions]);
  const marginTypeOptions = useMemo(
    () => getOptions("margin_type"),
    [getOptions]
  );
  const leverageOptions = useMemo(() => getOptions("leverage"), [getOptions]);
  const tradeTypeOptions = useMemo(
    () => getOptions(isSignal ? "signal_type" : "trade_type"),
    [getOptions]
  );

  const marginTypeToDisplay = useMemo(
    () => getOptionValueToDisplayMapping(marginTypeOptions),
    [marginTypeOptions]
  );

  const checkLeverageDisabled = (marginType: MarginTypes) =>
    marginType === MarginTypes.CROSS &&
    !isSignal &&
    !generalInfo?.cross_multiplier;

  const onExtraInfoChange = useCallback(
    (extraInfo: ExtraInfo) => {
      const titleFoundObject = checkOppositePositionError(extraInfo.reverse);
      if (quantity && titleFoundObject) {
        setQuantity("0");
        setQuantityPercent("0");
      }
      if (titleFoundObject && !showedOppositePositionError) {
        openConfirmModal(
          `You already have an open ${
            extraInfo.reverse ? "long" : "short"
          } ${titleFoundObject} on this symbol. Opening an opposite ${titleFoundObject} will close the previous ${titleFoundObject}. If you don't wish to close your previous ${titleFoundObject}, make sure to change the trade direction to ${
            extraInfo.reverse ? "long" : "short"
          }.`,
          () => undefined,
          undefined,
          true
        );
        setShowedOppositePositionError(true);
      }
      setExtraInfo(extraInfo);
    },
    [checkOppositePositionError, quantity, showedOppositePositionError]
  );

  const onChange = useCallback(
    (nameKey: string, value?: string | number | boolean | null) => {
      const updatedGeneralOptionsMapping: CreateTradeGeneralData = {
        ...generalOptionsMapping,
        [nameKey]: value,
      };
      if (
        nameKey === "margin_type" &&
        checkLeverageDisabled(value as MarginTypes) &&
        leverageOptions?.length > 0
      ) {
        updatedGeneralOptionsMapping.leverage = leverageOptions[0].value;
      }
      setGeneralOptionsMapping(updatedGeneralOptionsMapping);
      setValuesModified(true);
    },
    [generalOptionsMapping]
  );

  useEffect(() => {
    if (
      !isEqual(generalOptionsMapping, prevGeneralOptionsMapping) &&
      generalDataProcessed
    ) {
      const processedData = { ...generalOptionsMapping };
      onExtraInfoChange(processedData as ExtraInfo);
      if (
        Object.keys(prevGeneralOptionsMapping || {}).length &&
        (generalOptionsMapping?.reverse !==
          prevGeneralOptionsMapping?.reverse ||
          generalOptionsMapping?.trade_type !==
            prevGeneralOptionsMapping?.trade_type) &&
        !priceTargetsModified[OrderTypes.ENTRY] &&
        !priceTargetsModified[OrderTypes.TAKE_PROFIT]
      ) {
        setShouldUpdatePriceRange(OrderTypes.ENTRY, true);
        setShouldUpdatePriceRange(OrderTypes.TAKE_PROFIT, true);
        setShouldUpdateNumOfOrders(true);
      }
      setPrevGeneralOptionsMapping(processedData);
    }
  }, [
    generalOptionsMapping,
    prevGeneralOptionsMapping,
    generalDataProcessed,
    onExtraInfoChange,
  ]);

  const getMarginTypeValidationInfo: () => [string, boolean] | undefined =
    useCallback(() => {
      if (
        leverageLimits &&
        "margin_type" in leverageLimits &&
        generalOptionsMapping.margin_type !== leverageLimits["margin_type"]
      ) {
        return [
          `Margin type must be ${
            marginTypeToDisplay?.[leverageLimits.margin_type]
          }`,
          ErrorTypes.INVALID_LEVERAGE_MARGIN_TYPE in generalErrors,
        ];
      }
    }, [leverageLimits, marginTypeToDisplay, generalErrors]);

  const getLeverageValidationInfo: () => [string, boolean] | undefined =
    useCallback(() => {
      const maxLeverage = leverageOptions[leverageOptions.length - 1]?.value;
      if (
        !!maxLeverage &&
        !!generalOptionsMapping.leverage &&
        generalOptionsMapping.leverage > maxLeverage
      ) {
        return [
          `Leverage should be ${maxLeverage}x or less`,
          ErrorTypes.INVALID_LEVERAGE_MULTIPLIER in generalErrors,
        ];
      } else if (
        leverageLimits &&
        !!generalOptionsMapping.leverage &&
        "min_multiplier" in leverageLimits &&
        generalOptionsMapping.leverage < leverageLimits["min_multiplier"]
      )
        return [
          `Leverage should be ${leverageLimits.min_multiplier}x or more`,
          ErrorTypes.INVALID_LEVERAGE_MULTIPLIER in generalErrors,
        ];
    }, [leverageOptions, generalOptionsMapping, leverageLimits, generalErrors]);

  const getCancelOnTpErrorMessage = () => {
    if (ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_CURRENT_PRICE in generalErrors) {
      return t("messages.validation.tp_lower_short");
    } else if (
      ErrorTypes.LONG_TAKE_PROFIT_BELOW_CURRENT_PRICE in generalErrors
    ) {
      return t("messages.validation.tp_higher_long");
    } else if (
      ErrorTypes.SHORT_BREAKOUT_STOP_BELOW_CURRENT_PRICE in generalErrors
    ) {
      return t("messages.validation.stop_higher_short");
    } else if (
      ErrorTypes.LONG_BREAKOUT_STOP_ABOVE_CURRENT_PRICE in generalErrors
    ) {
      return t("messages.validation.stop_lower_long");
    }
  };

  const leverageData = {
    marginType: generalOptionsMapping.margin_type,
    marginTypeOptions,
    marginTypeValidationInfo: getMarginTypeValidationInfo(),
    leverage: generalOptionsMapping.leverage,
    leverageOptions,
    leverageValidationInfo: getLeverageValidationInfo(),
    leverageDisabled: checkLeverageDisabled(generalOptionsMapping.margin_type),
    onChange,
    errorMessageReason: t("messages.errors.open_position_exists", { symbol }),
  };
  const shouldDisplayLeverage = leverageOptions?.length > 0;

  const cancelOnTpData = {
    value: generalOptionsMapping.cancel_on_first_target_reached,
    onChange: (value: boolean | null) =>
      onChange("cancel_on_first_target_reached", value),
    isBreakout: generalOptionsMapping.trade_type === TradeTypes.BREAKOUT,
    errorMessage: getCancelOnTpErrorMessage(),
    shouldDisplay:
      generalOptionsMapping.cancel_on_first_target_reached !== undefined,
  };

  const longShortData = {
    options: longShortOptions.map((option) => option.name),
    value: generalOptionsMapping.reverse ? Direction.SHORT : Direction.LONG,
    onClick: (value: string | undefined) =>
      onChange("reverse", value === Direction.SHORT),
    shouldDisplay: longShortOptions?.length > 0,
  };

  const tradeTypeData = {
    options: tradeTypeOptions.map((option) => option.name),
    value: TRADE_TYPE_TO_DISPLAY_NAME[generalOptionsMapping.trade_type],
    onClick: (name: string | undefined) =>
      onChange("trade_type", findValueByName(tradeTypeOptions, name)),
  };

  return {
    showGeneralOptions,
    leverageData,
    cancelOnTpData,
    longShortData,
    tradeTypeData,
    shouldDisplayLeverage,
  };
};

export default useSharedGeneralTradeOptions;
